import { BotType, LanguageNameSpaces } from "@shared/types";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title.tsx";
import {
  ColoredDcaBotIcon,
  ColoredGridBotIcon,
  ColoredHedgingBotIcon,
  ColoredManualTradingIcon,
  ColoredSignalsBotIcon,
  ColoredTradingViewBotIcon,
  DarkColoredSignalsBotIcon,
} from "common/Icons/Icons.tsx";
import Divider from "common/misc/Divider.tsx";
import { useHistory } from "react-router-dom";
import { CREATE_TRADE_PATH } from "common/constants.ts";
import { getCreateBotRoute } from "utils/routes.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import { getIsDarkMode } from "utils/misc.tsx";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import React from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import {
  useSelectUser,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import Modal from "common/Modal/Modal.tsx";
import { useCanManageBotType } from "@shared/features/bots/hooks.ts";
import { HEDGING_BOTS_FEATURE_FLAG } from "@shared/common/constants.ts";
import { getCreateBotModalBotTypeId } from "testIds.ts";
import { ChevronRight, LucideIcon, Monitor } from "lucide-react";
import IconTile, { Tone } from "common/NewLook/IconTile.tsx";
import { NEW_LOOK_BOT_TYPE_TO_ICON } from "common/NewLook/botTypeIcons.ts";
import { phoneMaxWidth, phoneMinWidth } from "common/NewLook/layout.ts";

interface CreateBotModalProps {
  open: boolean;
  closeModal: () => void;
}

const CreateBotModal = ({ open, closeModal }: CreateBotModalProps) => {
  const theme = useTheme();
  const history = useHistory();
  const isDarkMode = getIsDarkMode();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isSubUser } = useSubUserPermissions();
  const canCreateSignalsBot = useCanManageBotType(BotType.SIGNAL);
  const user = useSelectUser();
  const hasHedgingFeatureFlag = user?.hasFeatureFlag(HEDGING_BOTS_FEATURE_FLAG);

  const botTypeToIcon = {
    [BotType.SIGNAL]: isDarkMode
      ? DarkColoredSignalsBotIcon
      : ColoredSignalsBotIcon,
    [BotType.ASAP]: ColoredDcaBotIcon,
    [BotType.GRID]: ColoredGridBotIcon,
    [BotType.TRADING_VIEW]: ColoredTradingViewBotIcon,
    [BotType.HEDGING]: ColoredHedgingBotIcon,
  };
  const iconStyle = {
    width: isBottomNavigator ? 45 : 55,
    height: isBottomNavigator ? 28 : 38,
  };

  const getRow = (
    icon: React.JSX.Element,
    title: string,
    description: string,
    testId?: string,
    onClick?: () => void
  ) => {
    return (
      <RowWrapper
        data-testid={testId}
        onClick={onClick}
        $isDarkMode={isDarkMode}
        key={title}
      >
        <IconBg $isDarkMode={isDarkMode}>{icon}</IconBg>
        <div>
          <Title level={3}>{title}</Title>
          <Text type="medium">{description}</Text>
        </div>
      </RowWrapper>
    );
  };

  const getNewLookRow = (
    icon: LucideIcon,
    tone: Tone,
    title: string,
    description: string,
    testId?: string,
    onClick?: () => void
  ) => (
    <NewLookRow
      type="button"
      data-testid={testId}
      onClick={onClick}
      key={title}
    >
      <IconTile icon={icon} tone={tone} size={40} />
      <NewLookRowText>
        <NewLookRowTitle>{title}</NewLookRowTitle>
        <NewLookRowDescription>{description}</NewLookRowDescription>
      </NewLookRowText>
      <ChevronRight className="chevron" size={16} strokeWidth={1.75} />
    </NewLookRow>
  );

  const handleClick = (botType?: BotType) => {
    closeModal();
    if (botType) {
      history.push(getCreateBotRoute(botType));
    } else {
      history.push(CREATE_TRADE_PATH);
    }
  };

  const title = t("create_bot_modal.create_new_bot");
  const botTypes = (
    isSubUser
      ? canCreateSignalsBot
        ? [BotType.SIGNAL]
        : []
      : [
          BotType.SIGNAL,
          BotType.ASAP,
          BotType.GRID,
          BotType.HEDGING,
          BotType.TRADING_VIEW,
        ]
  ).filter((botType) => {
    return hasHedgingFeatureFlag || botType !== BotType.HEDGING;
  });

  const newLookContent = (
    <NewLookList>
      {botTypes.map((botType) =>
        getNewLookRow(
          NEW_LOOK_BOT_TYPE_TO_ICON[botType],
          "blue",
          t(
            BOT_TYPE_TO_DISPLAY_NAME[
              botType
            ] as TranslationKeys<LanguageNameSpaces.COMMON>
          ),
          t(
            BOT_TYPE_TO_DESCRIPTION[
              botType
            ] as TranslationKeys<LanguageNameSpaces.COMMON>
          ),
          getCreateBotModalBotTypeId(botType),
          () => handleClick(botType)
        )
      )}
      {!isSubUser && (
        <>
          <Divider
            text={t("create_bot_modal.or")}
            style={{ margin: "4px 0" }}
          />
          {getNewLookRow(
            Monitor,
            "neutral",
            t("create_bot_modal.title.manual"),
            t("create_bot_modal.description.manual"),
            undefined,
            () => handleClick()
          )}
        </>
      )}
    </NewLookList>
  );

  const classicContent = (
    <>
      {botTypes.map((botType) => {
        const Icon = botTypeToIcon[botType];
        return getRow(
          <Icon style={iconStyle} />,
          t(
            BOT_TYPE_TO_DISPLAY_NAME[
              botType
            ] as TranslationKeys<LanguageNameSpaces.COMMON>
          ),
          t(
            BOT_TYPE_TO_DESCRIPTION[
              botType
            ] as TranslationKeys<LanguageNameSpaces.COMMON>
          ),
          getCreateBotModalBotTypeId(botType),
          () => handleClick(botType)
        );
      })}
      {!isSubUser && (
        <>
          <Divider
            text={t("create_bot_modal.or")}
            style={{ margin: isBottomNavigator ? 0 : "12px 0" }}
          />
          {getRow(
            <ColoredManualTradingIcon style={iconStyle} />,
            t("create_bot_modal.title.manual"),
            t("create_bot_modal.description.manual"),
            undefined,
            () => handleClick()
          )}
        </>
      )}
    </>
  );
  const content = theme.isNewLook ? newLookContent : classicContent;

  if (isBottomNavigator) {
    return (
      <BottomDrawer
        open={open}
        onClose={closeModal}
        title={title}
        content={content}
        contentStyle={{ maxHeight: "90vh", overflowY: "auto" }}
        rightButtons={
          <CloseOutlined
            onClick={closeModal}
            style={{ color: theme.colors.primaryGrey }}
          />
        }
      />
    );
  }
  return (
    <Modal
      title={title}
      open={open}
      onCancel={closeModal}
      footer={null}
      width={theme.isNewLook ? NEW_LOOK_MODAL_WIDTH : undefined}
      styles={{ body: { padding: theme.isNewLook ? "12px 24px 24px" : 16 } }}
    >
      {content}
    </Modal>
  );
};

export default React.memo(CreateBotModal);

const BOT_TYPE_TO_DISPLAY_NAME: {
  [key in BotType]?: TranslationKeys<LanguageNameSpaces.COMMON>;
} = {
  [BotType.SIGNAL]: "create_bot_modal.title.signal",
  [BotType.ASAP]: "create_bot_modal.title.dca",
  [BotType.GRID]: "create_bot_modal.title.grid",
  [BotType.TRADING_VIEW]: "create_bot_modal.title.trading_view",
  [BotType.HEDGING]: "create_bot_modal.title.hedging",
};
const BOT_TYPE_TO_DESCRIPTION: {
  [key in BotType]?: TranslationKeys<LanguageNameSpaces.COMMON>;
} = {
  [BotType.SIGNAL]: "create_bot_modal.description.signal",
  [BotType.ASAP]: "create_bot_modal.description.dca",
  [BotType.GRID]: "create_bot_modal.description.grid",
  [BotType.TRADING_VIEW]: "create_bot_modal.description.trading_view",
  [BotType.HEDGING]: "create_bot_modal.description.hedging",
};

const RowWrapper = styled(Flex)<{ $isDarkMode?: boolean }>`
  justify-content: flex-start;
  cursor: pointer;
  padding: 16px;
  @media (min-width: ${phoneMinWidth}) {
    &:hover {
      background: ${(p) =>
        p.$isDarkMode ? "#374762" : p.theme.colors.lightBlue};
      border-radius: 12px;
    }
  }
  @media (max-width: ${phoneMaxWidth}) {
    padding: 16px 0;
    &:first-child {
      padding-top: 0;
    }
  }
`;
const IconBg = styled.div<{ $isDarkMode?: boolean }>`
  background: ${(p) => (p.$isDarkMode ? "#374762" : p.theme.colors.lightBlue)};
  border-radius: 15px;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  @media (max-width: ${phoneMaxWidth}) {
    width: 45px;
    height: 45px;
    border-radius: 12px;
  }
`;

const NEW_LOOK_MODAL_WIDTH = 540;
const NewLookList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const NewLookRow = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  border: ${(p) => p.theme.borders.card};
  border-radius: ${(p) => p.theme.radii.card - 2}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
  transition: border-color 150ms, background-color 150ms;
  .chevron {
    flex: none;
    margin-left: auto;
    color: ${(p) => p.theme.colors.lightestText};
    transition: transform 150ms, color 150ms;
  }
  &:hover {
    border-color: ${(p) => p.theme.colors.midBlue};
    background: ${(p) => p.theme.colors.cardLightBgColor};
    .chevron {
      color: ${(p) => p.theme.colors.primaryBlue};
      transform: translateX(2px);
    }
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 2px;
  }
`;
const NewLookRowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NewLookRowTitle = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookRowDescription = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 1.45;
`;
